import { z } from 'zod';

// Recipient validation schema
export const recipientSchema = z.object({
  recipientName: z
    .string()
    .trim()
    .min(1, 'Recipient name is required')
    .max(100, 'Recipient name must be less than 100 characters'),
  contactType: z.enum(['phone', 'email', 'instagram', 'facebook', 'twitter'], {
    errorMap: () => ({ message: 'Invalid contact type' }),
  }),
  contactValue: z
    .string()
    .trim()
    .min(1, 'Contact info is required')
    .max(200, 'Contact info must be less than 200 characters'),
  relationship: z.enum(
    ['parent', 'grandparent', 'sibling', 'child', 'spouse', 'friend', 'mentor', 'colleague', 'other'],
    { errorMap: () => ({ message: 'Invalid relationship type' }) }
  ),
  frequency: z.enum(['daily', 'weekly', 'biweekly', 'monthly'], {
    errorMap: () => ({ message: 'Invalid frequency' }),
  }),
  notes: z
    .string()
    .trim()
    .max(500, 'Notes must be less than 500 characters')
    .optional()
    .transform((val) => val || ''),
});

export type RecipientFormData = z.infer<typeof recipientSchema>;

// Important date validation schema
export const importantDateSchema = z.object({
  recipient_name: z
    .string()
    .trim()
    .min(1, 'Recipient name is required')
    .max(100, 'Recipient name must be less than 100 characters'),
  date_type: z.enum(['birthday', 'relationship_anniversary', 'friendship_anniversary', 'other'], {
    errorMap: () => ({ message: 'Invalid date type' }),
  }),
  date_value: z.date({
    required_error: 'Date is required',
    invalid_type_error: 'Invalid date',
  }),
  notes: z
    .string()
    .trim()
    .max(500, 'Notes must be less than 500 characters')
    .optional()
    .transform((val) => val || ''),
});

export type ImportantDateFormData = z.infer<typeof importantDateSchema>;

// Sanitization helpers for AI prompts
export const sanitizeForAIPrompt = (text: string, maxLength: number = 100): string => {
  return text
    .replace(/[\n\r]/g, ' ') // Remove newlines that could break prompt structure
    .replace(/[<>]/g, '') // Remove potential HTML/XML injection
    .trim()
    .substring(0, maxLength);
};

export const sanitizeName = (name: string): string => sanitizeForAIPrompt(name, 100);
export const sanitizeNotes = (notes: string): string => sanitizeForAIPrompt(notes, 500);
